import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { ToastContainer, toast } from "react-toastify";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  Plus,
  Search,
  Users,
  Briefcase,
  User,
  MapPin,
  CreditCard,
  Edit,
  Trash2,
  BarChart2,
  Eye,
  Mail,
  Percent,
  History,
  RotateCcw,
  XCircle,
  Clock,
  RefreshCw,
  Download, // Export
  Upload,   // Import
  CheckCircle,
  FileSpreadsheet,
  FileWarning,
  ArrowLeft,
  ChevronRight,
  Save,
  Check,
  X,
  Phone,
  Tag,
  ShieldCheck,
  RefreshCcw,
  Filter
} from "lucide-react";
import { format } from "date-fns";
import * as XLSX from "xlsx";

import Loader from "../../../loader/Loader";
import AddCustomer from "./AddCustomer";
import ViewCustomer from "./ViewCustomer";
import AuditInfoModal, { buildAuditRows } from "../../../shared/AuditInfoModal";
import Pagination from "../../Pagination";
import { domain, domainAccounting } from "../../../../security";
import { selectUserID } from "../../../../redux/IchthusSlice";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const PRICE_TYPE_MAP = {
  "VAT Exclusive": "vatEx",
  "VAT Inclusive": "vatInc",
  "Reseller": "reseller",
  "Zero Rated": "zeroRated",
  // Fallbacks if user left system keys:
  vatEx: "vatEx",
  vatInc: "vatInc",
  reseller: "reseller",
  zeroRated: "zeroRated",
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  return format(new Date(dateString), "MMM dd, yyyy");
};

// ==========================================
// FORMATTER FOR BALANCE
// ==========================================
const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency: "PHP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount || 0);
};

// ==========================================
// SYNC CONFIRMATION MODAL
// ==========================================
const SyncConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  customerCount,
  syncing,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col transform transition-all">
        {/* Header */}
        <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-white">
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <div className="p-2 bg-emerald-50 rounded-full text-emerald-600">
              <RefreshCw size={18} className={syncing ? "animate-spin" : ""} />
            </div>
            Sync to Accounting
          </h2>
          <button
            onClick={onClose}
            disabled={syncing}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition disabled:opacity-50"
          >
            <XCircle size={22} />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 bg-slate-50/50">
          <div className="bg-white p-4 rounded-xl border border-slate-200 mb-2">
            <p className="text-sm text-slate-600 leading-relaxed text-center">
              Are you sure you want to sync <br />
              <strong className="text-xl text-emerald-600">
                {customerCount}
              </strong>{" "}
              <br />
              customers to the Accounting system?
            </p>
            <p className="text-xs text-slate-400 text-center mt-3">
              This will securely push all current customer data to the
              centralized accounting database.
            </p>
          </div>

          {/* Footer Actions */}
          <div className="flex justify-end gap-3 pt-5">
            <button
              onClick={onClose}
              disabled={syncing}
              className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={syncing}
              className="px-4 py-2 flex items-center gap-2 text-sm font-semibold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors shadow-sm disabled:opacity-70"
            >
              {syncing ? (
                <>
                  <RefreshCw size={16} className="animate-spin" /> Syncing...
                </>
              ) : (
                "Confirm Sync"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// HISTORY MODAL
// ==========================================
const DeletedHistoryModal = ({ isOpen, onClose, onRestore }) => {
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [auditItem, setAuditItem] = useState(null);

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${domain}/api/Customers/history?search=${search}`,
      );
      setHistoryData(response.data);
    } catch {
      toast.error("Failed to load customer history.");
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    if (isOpen) {
      fetchHistory();
      setCurrentPage(1);
    }
  }, [isOpen, fetchHistory]);

  if (!isOpen) return null;

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = historyData.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-slate-100 flex justify-between items-center bg-white">
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <div className="p-2 bg-red-50 rounded-full text-red-500">
              <Trash2 size={18} />
            </div>
            Deleted Customer History
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition"
          >
            <XCircle size={22} />
          </button>
        </div>

        {/* Search */}
        <div className="px-4 py-3 bg-slate-50/50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search deleted customers..."
              className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-red-100 focus:border-red-400 text-sm outline-none transition-all"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 bg-slate-50/50">
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader />
            </div>
          ) : historyData.length === 0 ? (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-100 mb-3">
                <History size={20} className="text-slate-400" />
              </div>
              <p className="text-slate-500 text-sm">
                No deleted customers found.
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {/* Desktop Table */}
              <div className="hidden sm:block bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <table className="w-full text-sm text-left text-slate-600">
                  <thead className="text-xs text-slate-500 uppercase bg-slate-50">
                    <tr>
                      <th className="px-4 py-3">Customer Name</th>
                      <th className="px-4 py-3">Type</th>
                      <th className="px-4 py-3">Deleted By</th>
                      <th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {currentItems.map((item) => (
                      <tr key={item.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-medium text-slate-800 flex items-center gap-1">
                          {item.customerName}
                          <button
                            onClick={() => setAuditItem(item)}
                            className="text-amber-500 hover:text-amber-700 ml-1"
                            title="View Audit Info"
                          >
                            <Clock size={14} />
                          </button>
                        </td>
                        <td className="px-4 py-3 text-xs">
                          <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full">
                            {item.customerType}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs">
                          <span className="font-medium text-red-500">
                            {item.deletedByFullName || "Unknown"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-red-500 text-xs">
                          {formatDate(item.deletedDate)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={async () => {
                              await onRestore(item.id);
                              fetchHistory();
                            }}
                            className="text-green-600 hover:text-green-700 font-semibold text-xs flex items-center justify-end gap-1 ml-auto"
                          >
                            <RotateCcw size={14} /> Restore
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Stack */}
              <div className="sm:hidden flex flex-col gap-3">
                {currentItems.map((item) => (
                  <div
                    key={item.id}
                    className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h4 className="font-semibold text-slate-800">
                          {item.customerName}
                        </h4>
                        <span className="text-xs text-slate-400">
                          {item.customerType}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setAuditItem(item)}
                          className="p-2 bg-amber-50 text-amber-500 rounded-lg hover:bg-amber-100 transition"
                        >
                          <Clock size={16} />
                        </button>
                        <button
                          onClick={async () => {
                            await onRestore(item.id);
                            fetchHistory();
                          }}
                          className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition"
                        >
                          <RotateCcw size={18} />
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 text-xs text-slate-500">
                      <span className="flex items-center gap-1">
                        <Trash2 size={10} className="text-red-400" />
                        Deleted by:{" "}
                        <span className="font-medium text-red-500">
                          {item.deletedByFullName || "Unknown"}
                        </span>
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={10} className="text-slate-400" />
                        {formatDate(item.deletedDate)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-2">
                <Pagination
                  itemsPerPage={itemsPerPage}
                  totalItems={historyData.length}
                  currentPage={currentPage}
                  paginate={(p) => setCurrentPage(p)}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <AuditInfoModal
        isOpen={!!auditItem}
        onClose={() => setAuditItem(null)}
        title={auditItem?.customerName}
        rows={buildAuditRows(auditItem)}
      />
    </div>
  );
};

// ==========================================
// MOBILE CARD (IMPROVED BALANCE BLOCK)
// ==========================================
const CustomerCard = ({ customer, onView, onEdit, onDelete, onAudit }) => (
  <div className="bg-white p-4 rounded-lg shadow-sm border border-slate-200">
    <div className="flex justify-between items-start mb-3">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600">
          <User size={18} />
        </div>
        <div>
          <span className="font-bold text-slate-800 text-lg block">
            {customer.customerName}
          </span>
          <span className="text-[10px] font-mono text-slate-400 block">
            {customer.customerCode}
          </span>
          <div className="flex gap-1 flex-wrap mt-0.5">
            <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full inline-block">
              {customer.customerType}
            </span>
            {customer.priceType && (
              <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full inline-block">
                {customer.priceType}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>

    {/* IMPROVED BALANCE BLOCK */}
    <div className="mb-4 bg-slate-50 rounded-xl border border-slate-100 p-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CreditCard size={16} className="text-slate-400" />
          <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
            Available Credit
          </span>
        </div>
        <span
          className={`text-base font-bold ${
            customer.availableBalance < 0 ? "text-red-600" : "text-emerald-600"
          }`}
        >
          {formatCurrency(customer.availableBalance)}
        </span>
      </div>
    </div>

    <div className="space-y-2 text-sm text-slate-600 mb-3 border-t border-slate-100 pt-3">
      <div className="flex items-center gap-2">
        <Mail size={14} className="text-slate-400" />
        <span className="truncate">{customer.email || "N/A"}</span>
      </div>
      <div className="flex items-center gap-2">
        <MapPin size={14} className="text-slate-400" />
        <span className="truncate">{customer.address || "No Address"}</span>
      </div>
      <div className="flex items-center gap-2">
        <CreditCard size={14} className="text-slate-400" />
        <span className="font-mono text-xs">
          {customer.tinNumber || "No TIN"}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <Percent size={14} className="text-slate-400" />
        <span>EWT: </span>
        <span
          className={`px-2 py-0.5 rounded text-xs font-semibold ${
            customer.ewt === true
              ? "bg-orange-100 text-orange-700"
              : "bg-gray-100 text-gray-600"
          }`}
        >
          {customer.ewt ? "Yes" : "No"}
        </span>
      </div>
      <div className="flex items-center gap-2 text-xs text-slate-400">
        <User size={10} />
        {customer.createdByFullName || "System"} ·{" "}
        {formatDate(customer.createdDate)}
      </div>
    </div>

    {/* Actions */}
    <div className="flex gap-2 pt-3 border-t border-slate-100 flex-wrap">
      <button
        onClick={() => onAudit(customer)}
        className="flex-1 py-2 border border-amber-100 text-amber-600 rounded hover:bg-amber-50 text-xs font-medium flex justify-center items-center gap-1"
      >
        <Clock size={14} /> Audit
      </button>

      <button
        onClick={() => onView(customer.id)}
        className="flex-1 py-2 border border-blue-100 text-blue-600 rounded hover:bg-blue-50 text-xs font-medium flex justify-center items-center gap-1"
      >
        <Eye size={14} /> View
      </button>
      <button
        onClick={() => onEdit(customer)}
        className="flex-1 py-2 border border-indigo-100 text-indigo-600 rounded hover:bg-indigo-50 text-xs font-medium flex justify-center items-center gap-1"
      >
        <Edit size={14} /> Edit
      </button>
      <button
        onClick={() => onDelete(customer.id)}
        className="flex-1 py-2 border border-red-100 text-red-600 rounded hover:bg-red-50 text-xs font-medium flex justify-center items-center gap-1"
      >
        <Trash2 size={14} /> Delete
      </button>
    </div>
  </div>
);

// ==========================================
// MAIN COMPONENT
// ==========================================
const AllCustomers = () => {
  const currentUserID = useSelector(selectUserID);

  const [customerData, setCustomerData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [customerToEdit, setCustomerToEdit] = useState(null);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [customerToViewId, setCustomerToViewId] = useState(null);
  const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);
  const [auditItem, setAuditItem] = useState(null);

  // Sync state
  const [isSyncModalVisible, setIsSyncModalVisible] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  const [viewMode, setViewMode] = useState("Walk-In");
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // --- Fetch ---
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${domain}/api/Customers`);
      setCustomerData(response.data);
    } catch {
      toast.error("Failed to fetch customers.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // --- Filter ---
  useEffect(() => {
    const results = customerData.filter((item) => {
      const matchesType = (item.customerType || "Walk-In") === viewMode;
      const lower = searchQuery.toLowerCase();
      const matchesSearch =
        (item.customerName || "").toLowerCase().includes(lower) ||
        (item.email || "").toLowerCase().includes(lower) ||
        (item.tinNumber || "").toLowerCase().includes(lower);
      return matchesType && matchesSearch;
    });
    setFilteredCustomers(results);
    setCurrentPage(1);
  }, [viewMode, searchQuery, customerData]);

  // --- Soft Delete ---
  const handleSoftDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this customer?"))
      return;
    try {
      await axios.put(
        `${domain}/api/Customers/soft-delete/${id}`,
        { userID: currentUserID },
        { headers: { "Content-Type": "application/json" } },
      );
      toast.success("Customer moved to history!");
      fetchData();
    } catch {
      toast.error("Failed to delete customer.");
    }
  };

  // --- Restore ---
  const handleRestore = async (id) => {
    try {
      await axios.put(
        `${domain}/api/Customers/restore/${id}`,
        { userID: currentUserID },
        { headers: { "Content-Type": "application/json" } },
      );
      toast.success("Customer restored successfully!");
      fetchData();
    } catch {
      toast.error("Failed to restore customer.");
    }
  };

  // ==========================================
  // SYNC ACTION LOGIC
  // ==========================================
  const handleSyncToAccounting = async () => {
    if (!customerData || customerData.length === 0) {
      toast.error("No customers available to sync.");
      return;
    }

    try {
      setIsSyncing(true);

      const payload = customerData.map((c) => {
        const numericUserId = currentUserID ? parseInt(currentUserID, 10) : 0;

        return {
          customerId: c.customerCode || c.id?.toString() || "",
          name1: c.customerName || "",
          name2: c.businessStyle || "",
          street: c.address || "",
          cityId: c.cityId || "",
          districtId: c.districtId || "",
          countryId: c.countryId || "",
          regionId: c.regionId || "",
          zip: c.zipCode || c.zip || "",
          mobile: c.contactNumber || c.mobile || "",
          telephone: c.telephone || "",
          tin: c.tinNumber || c.tin || "",
          fax: c.fax || "",
          email: c.email || "",
          searchTerm: c.customerType || c.contactPerson || "",
          isEWT: !!c.ewt,
          priceType: c.priceType === "vatEx" ? "Exclusive" :
              c.priceType === "vatInc" ?  "Inclusive" :
              c.priceType === "reseller" ?  "Reseller" :
              c.priceType === "zeroRated" ?  "Zero Rated" :
              "",
          terms: c.termsDescription,
          salesman: c.salesman,
          salesmans: c.salesmans.map((x) => {
            return {
              salesmanCode: `SM-${x.id}`,
              name: x.name,
            }
          }),
          certificateOfRegistration: c.certificateOfRegistration || "",
          createdDate: c.createdDate || new Date().toISOString(),
          createdBy: isNaN(numericUserId) ? 0 : numericUserId,
        };
      });

      const endpoint = `${domainAccounting}/api/Customer/bulk`;

      await axios.post(endpoint, payload, {
        headers: { "Content-Type": "application/json" },
      });

      toast.success(
        `Successfully synced ${payload.length} customers to Accounting!`,
      );
      setIsSyncModalVisible(false);
    } catch (error) {
      console.error("Accounting Sync Error:", error);
      toast.error("Failed to sync customers to Accounting.");
    } finally {
      setIsSyncing(false);
    }
  };

  // --- Modal Handlers ---
  const openModal = (customer = null) => {
    setCustomerToEdit(customer);
    setIsModalVisible(true);
  };

  const closeModal = () => {
    setIsModalVisible(false);
    setCustomerToEdit(null);
    fetchData();
  };

  const openViewModal = (id) => {
    setCustomerToViewId(id);
    setIsViewModalVisible(true);
  };

  const closeViewModal = () => {
    setIsViewModalVisible(false);
    setCustomerToViewId(null);
  };

  // --- Pagination ---
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentCustomers = filteredCustomers.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );

  const fetchTermsData = useCallback(async () => {
    try {
      const response = await axios.get(`${domain}/api/Terms` , {
          headers: { 'Content-Type': 'application/json'}
      })
        
      return (response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
      return []
    }
  }, []);

  const priceTypes = [
    {value: "vatEx", name: "VAT Exclusive"},
    {value: "vatInc", name: "VAT Inclusive"},
    {value: "reseller", name: "Reseller"},
    {value: "zeroRated", name: "Zero Rated"},
  ]

  const exportHandleClick = async () => {
    if (!customerData?.length) return;

    setLoading(true);

    try {
      const terms = (await fetchTermsData()) || [];

      // Extract list of term descriptions
      const termsList = terms
        .map((t) => (typeof t === "string" ? t : t?.description))
        .filter(Boolean);

      const priceTypeListString = `"${priceTypes.map((p) => p.name).join(",")}"`;

      const workbook = new ExcelJS.Workbook();

      // ==========================================
      // TAB 1: CUSTOMERS
      // ==========================================
      const customerSheet = workbook.addWorksheet("Customers", {
        views: [{ showGridLines: true }],
      });

      // Column Map:
      // A: CustomerCode, B: CustomerName, C: CustomerType, D: Address,
      // E: BusinessStyle, F: Email, G: MobileNumber, H: TINNumber,
      // I: PriceType (Col 9), J: EWT (Col 10), K: TermsDescription (Col 11)
      customerSheet.columns = [
        { header: "Customer Code", key: "CustomerCode" },
        { header: "Customer Name", key: "CustomerName" },
        { header: "Customer Type", key: "CustomerType" },
        { header: "Address", key: "Address" },
        { header: "Business Style", key: "BusinessStyle" },
        { header: "Email", key: "Email" },
        { header: "Mobile Number", key: "MobileNumber" },
        { header: "TIN Number", key: "TINNumber" },
        { header: "Price Type", key: "PriceType" },
        { header: "EWT", key: "EWT" },
        { header: "Terms Description", key: "TermsDescription" },
      ];

      // Center-align the entire EWT column
      customerSheet.getColumn("EWT").alignment = { horizontal: "center" };

      // Store Terms in a hidden lookup sheet to bypass Excel's 255-character inline limit
      if (termsList.length > 0) {
        const lookupSheet = workbook.addWorksheet("Lists");
        lookupSheet.state = "hidden"; // Hide lookup sheet from normal user view
        termsList.forEach((desc, idx) => {
          lookupSheet.getCell(`A${idx + 1}`).value = desc;
        });
      }

      // Add Customer Data Rows
      customerData.forEach((i) => {
        // 1. Map Price Type value -> name
        const matchedPriceType = priceTypes.find(
          (pt) => { 
            return pt.value === i.priceType
          }
        );
        const priceTypeName = matchedPriceType
          ? matchedPriceType.name
          : i.priceType ?? "";

        // 2. Map Terms description
        const matchedTerm = terms.find(
          (t) => { 
            return (
              t.id === i.termsId )
        });

        const termsDescription =
          matchedTerm?.description ?? "";

        customerSheet.addRow({
          CustomerCode: i.customerCode ?? "",
          CustomerName: i.customerName ?? "",
          CustomerType: i.customerType ?? "",
          Address: i.address ?? "",
          BusinessStyle: i.businessStyle ?? "",
          Email: i.email ?? "",
          MobileNumber: i.mobileNumber ?? "",
          TINNumber: i.tinNumber ?? "",
          PriceType: priceTypeName,
          EWT: i.ewt ? "Yes" : "No",
          TermsDescription: termsDescription,
        });
      });

      // =========================================================================
      // APPLY RANGE-WIDE DATA VALIDATION (Entire Column: Rows 2 to 9999)
      // Works for all current data AND any new rows the user enters later.
      // =========================================================================

      // Column I: Price Type
      customerSheet.dataValidations.add("I2:I9999", {
        type: "list",
        allowBlank: true,
        formulae: [priceTypeListString],
        showErrorMessage: true,
        errorTitle: "Invalid Price Type",
        error: "Please select a valid Price Type from the dropdown.",
      });

      // Column K: Terms Description
      if (termsList.length > 0) {
        customerSheet.dataValidations.add("K2:K9999", {
          type: "list",
          allowBlank: true,
          formulae: [`Lists!$A$1:$A$${termsList.length}`],
          showErrorMessage: true,
          errorTitle: "Invalid Term",
          error: "Please select a valid Term Description from the dropdown.",
        });
      }

      // ==========================================
      // TAB 2: SALESMAN
      // ==========================================
      const salesmanSheet = workbook.addWorksheet("Salesman", {
        views: [{ showGridLines: true }],
      });

      salesmanSheet.columns = [
        { header: "Customer Code", key: "CustomerCode" },
        { header: "Customer Name", key: "CustomerName" },
        { header: "Salesman Name", key: "SalesmanName" },
      ];

      customerData.forEach((customer) => {
        (customer.salesmans ?? []).forEach((salesman) => {
          salesmanSheet.addRow({
            CustomerCode: customer.customerCode ?? "",
            CustomerName: customer.customerName ?? "",
            SalesmanName: salesman.name ?? "",
          });
        });
      });

      // ==========================================
      // STYLING & COLUMN MIN-WIDTH
      // ==========================================
      const applySheetFormatting = (sheet, minWidth = 16) => {
        const headerRow = sheet.getRow(1);
        headerRow.height = 26;
        headerRow.eachCell((cell) => {
          cell.font = { bold: true, color: { argb: "FFFFFF" }, size: 10 };
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "1E40AF" }, // Dark Navy Blue
          };
          cell.alignment = { vertical: "middle", horizontal: "center" };
        });

        sheet.columns.forEach((col) => {
          let maxLen = col.header ? col.header.toString().length : 10;
          col.eachCell({ includeEmpty: false }, (cell) => {
            const valStr = cell.value ? cell.value.toString() : "";
            if (valStr.length > maxLen) maxLen = valStr.length;
          });
          col.width = Math.max(maxLen + 4, minWidth);
        });
      };

      applySheetFormatting(customerSheet, 16);
      applySheetFormatting(salesmanSheet, 18);

      // ==========================================
      // EXPORT FILE
      // ==========================================
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, `Customer_Export_${new Date().getTime()}.xlsx`);
    } catch (error) {
      console.error("Export Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const [showUploadForm, setShowUploadForm] = useState(false);
  const openUploadForm = () => {
    setShowUploadForm(true)
  }
  const closeUploadForm = () => {
    setShowUploadForm(false)
  }



  return (
    <div className="min-h-screen pb-12">
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-20">
        <div className="max-w-[95%] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                <Users className="text-indigo-600" /> Customer Management
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                Manage walk-in customers and corporate clients.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 w-full md:w-auto">
              <button
                onClick={() => setIsSyncModalVisible(true)}
                className="flex-1 md:flex-none flex justify-center items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-600 hover:bg-emerald-100 text-sm font-medium px-4 py-2.5 rounded-xl transition-all"
              >
                <RefreshCw size={18} />
                <span>Sync to Accounting</span>
              </button>

              <button
                onClick={() => setIsHistoryModalVisible(true)}
                className="flex-1 md:flex-none flex justify-center items-center gap-2 bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100 text-sm font-medium px-4 py-2.5 rounded-xl transition-all"
              >
                <History size={18} />
                <span>History</span>
              </button>
              <button
                onClick={() => openModal()}
                className="flex-1 md:flex-none flex justify-center items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-5 py-2.5 rounded-xl shadow-md shadow-indigo-200 transition-all"
              >
                <Plus size={18} />
                Add Customer
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[95%] mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        {/* Stats & Type Toggle */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex items-center gap-4">
            <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
              <BarChart2 size={24} />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                Total {viewMode}s
              </p>
              <p className="text-2xl font-bold text-slate-800">
                {filteredCustomers.length}
              </p>
            </div>
          </div>

          <div className="md:col-span-2 bg-white p-2 rounded-2xl shadow-sm border border-slate-100 flex items-center">
            <div className="flex w-full p-1 bg-slate-100 rounded-xl">
              <button
                onClick={() => setViewMode("Walk-In")}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                  viewMode === "Walk-In"
                    ? "bg-indigo-600 text-white shadow"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <User size={16} /> Walk-In
              </button>
              <button
                onClick={() => setViewMode("Client")}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                  viewMode === "Client"
                    ? "bg-indigo-600 text-white shadow"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <Briefcase size={16} /> Client
              </button>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder={`Search ${viewMode} by Name, Email, or TIN...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border-none ring-1 ring-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all placeholder:text-slate-400"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-3 w-full md:w-auto py-2">
          {/* Export */}
          <button
            onClick={exportHandleClick}
            className="flex-1 md:flex-none flex justify-center items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-600 hover:bg-emerald-100 text-sm font-medium px-4 py-2.5 rounded-xl transition-all"
          >
            <Download size={18} />
            <span>Export</span>
          </button>

          {/* Import */}
          <button
            onClick={openUploadForm}
            className="flex-1 md:flex-none flex justify-center items-center gap-2 bg-blue-50 border border-blue-200 text-blue-600 hover:bg-blue-100 text-sm font-medium px-4 py-2.5 rounded-xl transition-all"
          >
            <Upload size={18} />
            <span>Import</span>
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader />
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              <table className="w-full text-sm text-left text-slate-600">
                <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-4 font-semibold">Customer Name</th>
                    <th className="px-6 py-4 font-semibold">Salesman</th>
                    <th className="px-6 py-4 font-semibold">Terms</th>
                    {/* <th className="px-6 py-4 font-semibold">Email</th>
                    <th className="px-6 py-4 font-semibold">TIN</th>
                    <th className="px-6 py-4 font-semibold text-center">EWT</th> */}
                    <th className="px-6 py-4 font-semibold">Credit Balance</th>
                    <th className="px-6 py-4 font-semibold">Created Info</th>
                    <th className="px-6 py-4 font-semibold text-right">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {currentCustomers.map((item) => {
                    return (
                      <tr key={item.id} className="hover:bg-slate-50 transition">
                        <td className="px-6 py-4">
                          <div className="font-bold text-slate-800">
                            {item.customerName}
                          </div>
                          <div className="text-[10px] font-mono text-slate-400">
                            {item.customerCode}
                          </div>
                          <div className="text-xs text-slate-400 mt-0.5 mb-1 flex items-center gap-1">
                            <span>{item.businessStyle || item.customerType}</span>
                            {item.priceType && (
                              <>
                                <span>•</span>
                                <span className="text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                                  {item.priceType}
                                </span>
                              </>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-2">
                            {item.salesmans?.map((man, index) => (
                              <span
                                key={index}
                                className="border border-gray-300 rounded-md px-2 py-1 text-sm"
                              >
                                {man.name ?? ""}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            {item.termsDescription || "-"}
                          </div>
                        </td>
                        {/* <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Mail size={14} className="text-slate-400" />
                            {item.email || "-"}
                          </div>
                        </td>
                        <td className="px-6 py-4 font-mono text-xs">
                          {item.tinNumber || "-"}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded text-xs font-semibold ${
                              item.ewt === true
                                ? "bg-orange-100 text-orange-700"
                                : "bg-slate-100 text-slate-500"
                            }`}
                          >
                            {item.ewt ? "Yes" : "No"}
                          </span>
                        </td> */}
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <CreditCard size={14} className="text-slate-400" />
                            <span
                              className={`font-semibold ${
                                item.availableBalance < 0
                                  ? "text-red-600"
                                  : "text-emerald-600"
                              }`}
                            >
                              {formatCurrency(item.availableBalance)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-xs">
                          <div className="flex flex-col">
                            <span className="font-semibold text-slate-700">
                              {item.createdByFullName || "System"}
                            </span>
                            <span className="text-slate-400">
                              {formatDate(item.createdDate)}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => setAuditItem(item)}
                              className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                              title="View Audit Info"
                            >
                              <Clock size={18} />
                            </button>

                            <button
                              onClick={() => openViewModal(item.id)}
                              className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="View Details"
                            >
                              <Eye size={18} />
                            </button>
                            <button
                              onClick={() => openModal(item)}
                              className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <Edit size={18} />
                            </button>
                            <button
                              onClick={() => handleSoftDelete(item.id)}
                              className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                  {currentCustomers.length === 0 && (
                    <tr>
                      <td
                        colSpan="7"
                        className="px-6 py-10 text-center text-slate-400"
                      >
                        No customers found matching your search.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="p-4 border-t border-slate-100 hidden md:block">
              <Pagination
                itemsPerPage={itemsPerPage}
                totalItems={filteredCustomers.length}
                currentPage={currentPage}
                paginate={(p) => setCurrentPage(p)}
              />
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden flex flex-col gap-3">
              {currentCustomers.map((item) => (
                <CustomerCard
                  key={item.id}
                  customer={item}
                  onView={openViewModal}
                  onEdit={openModal}
                  onDelete={handleSoftDelete}
                  onAudit={setAuditItem}
                />
              ))}
              {currentCustomers.length === 0 && (
                <div className="text-center py-10 text-slate-400 bg-white rounded-2xl border border-slate-100">
                  No customers found.
                </div>
              )}
              <div className="mt-2 pb-6">
                <Pagination
                  itemsPerPage={itemsPerPage}
                  totalItems={filteredCustomers.length}
                  currentPage={currentPage}
                  paginate={(p) => setCurrentPage(p)}
                />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Sync Confirm Modal */}
      <SyncConfirmModal
        isOpen={isSyncModalVisible}
        onClose={() => setIsSyncModalVisible(false)}
        onConfirm={handleSyncToAccounting}
        customerCount={customerData.length}
        syncing={isSyncing}
      />

      {/* Add/Edit Modal */}
      {isModalVisible && (
        <AddCustomer
          onClose={closeModal}
          refreshData={fetchData}
          customerToEdit={customerToEdit}
        />
      )}

      {/* View Modal */}
      {isViewModalVisible && (
        <ViewCustomer customerId={customerToViewId} onClose={closeViewModal} />
      )}

      {/* History Modal */}
      <DeletedHistoryModal
        isOpen={isHistoryModalVisible}
        onClose={() => {
          setIsHistoryModalVisible(false);
          fetchData();
        }}
        onRestore={handleRestore}
      />

      {/* Audit Modal */}
      <AuditInfoModal
        isOpen={!!auditItem}
        onClose={() => setAuditItem(null)}
        title={auditItem?.customerName}
        rows={buildAuditRows(auditItem)}
      />

      <UploadFormModal
        isOpen={showUploadForm}
        onClose={closeUploadForm}
        onUploadSuccess={fetchData}
      />
    </div>
  );
};

const UploadFormModal = ({ isOpen, onClose, onUploadSuccess }) => {
  // --- States ---
  const [fileData, setFileData] = useState([]);
  const [existingCustomers, setExistingCustomers] = useState([]);
  const [termsList, setTermsList] = useState([]);
  const [isSystemDataLoading, setIsSystemDataLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [step, setStep] = useState(1);
  const [note, setNote] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL"); // <-- NEW FILTER STATE
  const fileInputRef = useRef(null);

  const userID = useSelector((state) => state.auth?.userID || state.user?.userID);
  const fullName = useSelector((state) => state.auth?.fullName || state.user?.fullName);

  // --- Effects ---
  useEffect(() => {
    if (isOpen) {
      fetchPrerequisites();
      setStep(1);
      setFileData([]);
      setValidationErrors([]);
      setNote("");
      setStatusFilter("ALL");
    }
  }, [isOpen]);

  const fetchPrerequisites = async () => {
    setIsSystemDataLoading(true);
    try {
      const [customerRes, termsRes] = await Promise.all([
        axios.get(`${domain}/api/Customers`).catch(() => ({ data: [] })),
        axios.get(`${domain}/api/Terms`).catch(() => ({ data: [] })),
      ]);
      setExistingCustomers(customerRes.data || []);
      setTermsList(termsRes.data || []);
    } catch (error) {
      console.error("Failed to fetch initial references", error);
      toast.warning("Could not sync existing customers list.");
    } finally {
      setIsSystemDataLoading(false);
    }
  };

  // --- Helper to Normalize Values for Deep Comparison ---
  const normalizeStr = (val) => (val ?? "").toString().trim().toLowerCase();
  const normalizeBool = (val) => Boolean(val);

  // --- File Reader & Parser ---
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: "binary" });

        // Sheet 1: Customers
        const customerSheetName =
          wb.SheetNames.find((s) => s.toLowerCase().includes("customer")) ||
          wb.SheetNames[0];
        const customerWs = wb.Sheets[customerSheetName];
        const rawCustomerData = XLSX.utils.sheet_to_json(customerWs, { raw: false });

        if (rawCustomerData.length === 0) {
          toast.error("The Customers sheet is empty.");
          return;
        }

        // Sheet 2: Salesman
        const salesmanSheetName = wb.SheetNames.find((s) =>
          s.toLowerCase().includes("salesman")
        );
        let rawSalesmanData = [];
        if (salesmanSheetName && wb.Sheets[salesmanSheetName]) {
          rawSalesmanData = XLSX.utils.sheet_to_json(
            wb.Sheets[salesmanSheetName],
            { raw: false }
          );
        }

        processAndValidate(rawCustomerData, rawSalesmanData);
      } catch (error) {
        console.error("Parse Error:", error);
        toast.error("Failed to parse the Excel file.");
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = "";
  };

  // --- Validation, Mapping & Difference Checking ---
  const processAndValidate = (rawCustomers, rawSalesmen) => {
    const errors = [];
    const customerCodeSet = new Set();

    // 1. Group salesmen from uploaded Excel by Customer Code
    const salesmenByCustomer = {};
    rawSalesmen.forEach((row) => {
      const code = (row["Customer Code"] || row["CustomerCode"] || "").toString().trim();
      const name = (row["Salesman Name"] || row["SalesmanName"] || "").toString().trim();
      if (code && name) {
        if (!salesmenByCustomer[code]) salesmenByCustomer[code] = [];
        salesmenByCustomer[code].push(name);
      }
    });

    const formattedData = rawCustomers.map((row, index) => {
      const rowNum = index + 2;

      // Extract raw values
      const customerCode = (row["Customer Code"] || row["CustomerCode"] || "").toString().trim();
      const customerName = (row["Customer Name"] || row["CustomerName"] || "").toString().trim();
      const customerType = (row["Customer Type"] || row["CustomerType"] || "").toString().trim();
      const address = (row["Address"] || "").toString().trim();
      const businessStyle = (row["Business Style"] || row["BusinessStyle"] || "").toString().trim();
      const email = (row["Email"] || "").toString().trim();
      const mobileNumber = (row["Mobile Number"] || row["MobileNumber"] || "").toString().trim();
      const tinNumber = (row["TIN Number"] || row["TINNumber"] || "").toString().trim();
      const priceTypeRaw = (row["Price Type"] || row["PriceType"] || "").toString().trim();
      const priceType = PRICE_TYPE_MAP[priceTypeRaw] || priceTypeRaw || "vatEx";
      const ewtRaw = (row["EWT"] || "").toString().trim().toLowerCase();
      const ewt = ewtRaw === "yes" || ewtRaw === "true" || ewtRaw === "1";
      const termsDesc = (row["Terms Description"] || row["TermsDescription"] || "").toString().trim();

      // Find matching term ID
      const matchedTerm = termsList.find(
        (t) => normalizeStr(t.description) === normalizeStr(termsDesc)
      );

      // --- Validations ---
      if (!customerCode) {
        errors.push(`Row ${rowNum}: 'Customer Code' is required.`);
      } else if (customerCodeSet.has(customerCode.toLowerCase())) {
        errors.push(`Row ${rowNum}: Duplicate Customer Code '${customerCode}' found in file.`);
      } else {
        customerCodeSet.add(customerCode.toLowerCase());
      }

      if (!customerName) {
        errors.push(`Row ${rowNum}: 'Customer Name' is required.`);
      }

      // Check against existing system customers
      const existing = existingCustomers.find(
        (c) => normalizeStr(c.customerCode) === normalizeStr(customerCode)
      );

      // Get uploaded salesmen list for this customer
      const assignedSalesmen = salesmenByCustomer[customerCode] || [];

      // --- Change Detection ---
      let status = "NEW"; // NEW, UPDATED, or UNCHANGED
      let isSalesmanChanged = false;
      let isFieldsChanged = false;

      if (existing) {
        // Compare Salesmen list
        const existingSalesmenNames = (existing.salesmans || existing.salesmen || [])
          .map((s) => normalizeStr(s.name || s.salesmanName || s))
          .filter(Boolean)
          .sort();

        const newSalesmenNames = assignedSalesmen
          .map((name) => normalizeStr(name))
          .filter(Boolean)
          .sort();

        isSalesmanChanged =
          existingSalesmenNames.length !== newSalesmenNames.length ||
          existingSalesmenNames.some((name, i) => name !== newSalesmenNames[i]);

        // Compare Basic Customer Fields
        const updatedFields = [];

        const fieldsToCheck = [
          ["customerName", existing.customerName, customerName],
          ["customerType", existing.customerType, customerType],
          ["address", existing.address, address],
          ["businessStyle", existing.businessStyle, businessStyle],
          ["email", existing.email, email],
          ["mobileNumber", existing.mobileNumber, mobileNumber],
          ["tinNumber", existing.tinNumber, tinNumber],
          ["priceType", existing.priceType, priceType],
        ];

        fieldsToCheck.forEach(([field, oldValue, newValue]) => {
          if (normalizeStr(oldValue) !== normalizeStr(newValue)) {
            updatedFields.push({ field, oldValue, newValue });
          }
        });

        if (normalizeBool(existing.ewt) !== normalizeBool(ewt)) {
          updatedFields.push({
            field: "ewt",
            oldValue: existing.ewt,
            newValue: ewt,
          });
        }

        const oldTerm = matchedTerm?.id
          ? existing.termsId
          : existing.termsDescription;

        const newTerm = matchedTerm?.id ? matchedTerm.id : termsDesc;

        if (
          matchedTerm?.id
            ? oldTerm !== newTerm
            : normalizeStr(oldTerm) !== normalizeStr(newTerm)
        ) {
          updatedFields.push({
            field: matchedTerm?.id ? "termsId" : "termsDescription",
            oldValue: oldTerm,
            newValue: newTerm,
          });
        }

        isFieldsChanged = updatedFields.length > 0;
        status = isFieldsChanged || isSalesmanChanged ? "UPDATED" : "UNCHANGED";
      }

      return {
        customerCode,
        customerName,
        customerType,
        address,
        businessStyle,
        email,
        mobileNumber,
        tinNumber,
        priceType,
        priceTypeDisplay: priceTypeRaw,
        ewt,
        termsDescription: termsDesc,
        termsId: matchedTerm?.id || null,
        salesmans: assignedSalesmen.map((name) => ({ name })),
        status, // "NEW" | "UPDATED" | "UNCHANGED"
        isSalesmanChanged,
        isFieldsChanged,
        existingId: existing?.id || null,
      };
    });

    setValidationErrors(errors);
    setFileData(formattedData);
    setStatusFilter("ALL");
    setStep(2);
  };

  // --- Save Handler ---
  const handleFinalSave = async () => {
    setIsProcessing(true);
    try {
      const payload = {
        customers: fileData.map((item) => ({
          customerCode: item.customerCode,
          customerName: item.customerName,
          customerType: item.customerType,
          address: item.address,
          businessStyle: item.businessStyle,
          email: item.email,
          mobileNumber: item.mobileNumber,
          tinNumber: item.tinNumber,
          priceType: item.priceType,
          ewt: item.ewt,
          termsDescription: item.termsDescription,
          termsId: item.termsId,
          salesmans: item.salesmans,
          status: item.status,
        })),
        notes: note,
        uploadedBy: fullName,
        userId: userID,
      };

      await axios.post(`${domain}/api/Customers/upload-bulk`, payload);
      toast.success("Customers imported successfully!");

      if (onUploadSuccess) onUploadSuccess();
      handleClose();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Failed to upload customers.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setFileData([]);
    setStep(1);
    setStatusFilter("ALL");
    onClose();
  };

  // Status Counts
  const countNew = useMemo(() => fileData.filter((c) => c.status === "NEW").length, [fileData]);
  const countUpdated = useMemo(() => fileData.filter((c) => c.status === "UPDATED").length, [fileData]);
  const countUnchanged = useMemo(() => fileData.filter((c) => c.status === "UNCHANGED").length, [fileData]);

  // Filtered rows for Step 2 Table
  const filteredData = useMemo(() => {
    if (statusFilter === "ALL") return fileData;
    return fileData.filter((item) => item.status === statusFilter);
  }, [fileData, statusFilter]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4 transition-all">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl h-[90vh] flex flex-col overflow-hidden border border-slate-200 font-sans">
        {/* --- Header --- */}
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-white z-20">
          <div className="flex items-center gap-3">
            <div
              className={`p-2 rounded-lg ${
                step === 1
                  ? "bg-indigo-100 text-indigo-600"
                  : "bg-emerald-100 text-emerald-600"
              }`}
            >
              {step === 1 ? <Upload size={20} /> : <CheckCircle size={20} />}
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-800">
                Bulk Customer Upload
              </h3>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <span
                  className={`h-2 w-2 rounded-full ${
                    step >= 1 ? "bg-indigo-500" : "bg-slate-300"
                  }`}
                />
                <span>Upload</span>
                <span className="text-slate-300">/</span>
                <span
                  className={`h-2 w-2 rounded-full ${
                    step >= 2 ? "bg-indigo-500" : "bg-slate-300"
                  }`}
                />
                <span>Review</span>
                <span className="text-slate-300">/</span>
                <span
                  className={`h-2 w-2 rounded-full ${
                    step >= 3 ? "bg-indigo-500" : "bg-slate-300"
                  }`}
                />
                <span>Confirm</span>
              </div>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* --- Body Container --- */}
        <div className="flex-1 relative overflow-hidden bg-slate-50/50">
          {/* STEP 1: UPLOAD */}
          {step === 1 && (
            <div className="absolute inset-0 flex items-center justify-center p-8 animate-in fade-in zoom-in-95">
              <div
                className={`w-full max-w-2xl border-2 border-dashed rounded-2xl p-12 flex flex-col items-center justify-center text-center transition-all cursor-pointer bg-white ${
                  isSystemDataLoading
                    ? "border-slate-200 bg-slate-50"
                    : "border-indigo-200 hover:border-indigo-400 hover:bg-indigo-50/20 shadow-sm hover:shadow-md"
                }`}
                onClick={() => !isSystemDataLoading && fileInputRef.current?.click()}
              >
                <input
                  type="file"
                  accept=".xlsx, .xls"
                  className="hidden"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  disabled={isSystemDataLoading}
                />

                <div
                  className={`p-6 rounded-full mb-6 transition-all duration-500 ${
                    isSystemDataLoading ? "bg-slate-100" : "bg-indigo-50"
                  }`}
                >
                  {isSystemDataLoading ? (
                    <RefreshCw className="animate-spin text-slate-400" size={40} />
                  ) : (
                    <FileSpreadsheet className="text-indigo-600" size={40} />
                  )}
                </div>

                <h2 className="text-xl font-bold text-slate-800 mb-2">
                  {isSystemDataLoading
                    ? "Syncing Existing Customers..."
                    : "Upload Customer Excel File"}
                </h2>
                <p className="text-slate-500 text-sm max-w-md mb-8 leading-relaxed">
                  {isSystemDataLoading
                    ? "Loading current customers and terms to detect new, modified, and unchanged records..."
                    : "Upload an Excel file containing 'Customers' and optional 'Salesman' sheets to update records."}
                </p>

                <button
                  disabled={isSystemDataLoading}
                  className="px-8 py-3 bg-indigo-600 disabled:bg-slate-300 text-white font-semibold rounded-xl shadow-lg hover:bg-indigo-700 hover:shadow-indigo-200/50 transition-all transform active:scale-95"
                >
                  Select Excel File
                </button>
              </div>
            </div>
          )}

          {/* STEP 2: REVIEW TABLE */}
          {step === 2 && (
            <div className="absolute inset-0 flex flex-col animate-in slide-in-from-right-8 duration-300">
              {validationErrors.length > 0 && (
                <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-xl flex gap-3 shadow-sm shrink-0">
                  <FileWarning className="text-red-600 mt-0.5 shrink-0" size={18} />
                  <div className="flex-1 overflow-hidden">
                    <h4 className="font-bold text-red-900 text-sm">
                      Validation Issues ({validationErrors.length})
                    </h4>
                    <ul className="list-disc list-inside text-xs text-red-700 mt-1 max-h-20 overflow-y-auto">
                      {validationErrors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              <div className="flex-1 overflow-hidden p-6">
                <div className="bg-white border border-slate-200 rounded-xl shadow-sm h-full flex flex-col">
                  {/* --- Table Toolbar with Filter Dropdown --- */}
                  <div className="p-3 px-6 border-b border-slate-200 flex flex-wrap justify-between items-center gap-3 bg-white">
                    <div className="flex items-center gap-2">
                      <Users size={16} className="text-slate-500" />
                      <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                        Review List
                      </span>
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                        Showing {filteredData.length} of {fileData.length}
                      </span>
                    </div>

                    {/* Filter Dropdown */}
                    <div className="flex items-center gap-2">
                      <label className="text-xs font-semibold text-slate-500 flex items-center gap-1.5">
                        <Filter size={13} className="text-slate-400" />
                        Status Filter:
                      </label>
                      <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-3 py-1.5 text-xs font-semibold text-slate-700 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors cursor-pointer"
                      >
                        <option value="ALL">All ({fileData.length})</option>
                        <option value="NEW">New ({countNew})</option>
                        <option value="UPDATED">Updated ({countUpdated})</option>
                        <option value="UNCHANGED">Unchanged ({countUnchanged})</option>
                      </select>
                    </div>
                  </div>

                  {/* --- Table Content --- */}
                  <div className="flex-1 overflow-auto">
                    <table className="w-full text-left border-collapse">
                      <thead className="bg-slate-50 sticky top-0 z-20 shadow-sm text-xs font-bold text-slate-500 uppercase tracking-wider">
                        <tr>
                          <th className="p-4 border-b border-slate-200 sticky left-0 z-30 bg-slate-50 shadow-[4px_0_8px_-4px_rgba(0,0,0,0.1)]">
                            Customer Details
                          </th>
                          <th className="p-4 border-b border-slate-200">
                            Contact / Address
                          </th>
                          <th className="p-4 border-b border-slate-200 text-center">
                            TIN & Type
                          </th>
                          <th className="p-4 border-b border-slate-200 text-center">
                            Price & Terms
                          </th>
                          <th className="p-4 border-b border-slate-200">
                            Salesmen
                          </th>
                          <th className="p-4 border-b border-slate-200 text-center">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 text-sm">
                        {filteredData.length > 0 ? (
                          filteredData.map((row, i) => (
                            <tr
                              key={i}
                              className={`transition-colors ${
                                row.status === "NEW"
                                  ? "bg-green-50/20 hover:bg-green-50/40"
                                  : row.status === "UPDATED"
                                  ? "bg-amber-50/20 hover:bg-amber-50/40"
                                  : "hover:bg-slate-50/80"
                              }`}
                            >
                              {/* Customer Identifier */}
                              <td className="p-4 border-r border-slate-100 sticky left-0 z-10 bg-inherit shadow-[4px_0_8px_-4px_rgba(0,0,0,0.05)]">
                                <div className="flex flex-col">
                                  <span className="font-bold text-slate-800">
                                    {row.customerName}
                                  </span>
                                  <div className="flex items-center gap-2 mt-0.5">
                                    <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded border border-slate-200 font-mono font-bold">
                                      {row.customerCode}
                                    </span>
                                    {row.customerType && (
                                      <span className="text-[10px] text-slate-500 bg-slate-50 px-1 py-0.5 rounded border border-slate-200">
                                        {row.customerType}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </td>

                              {/* Contact & Address */}
                              <td className="p-4 border-r border-slate-100 max-w-[250px]">
                                <div className="flex flex-col gap-1 text-xs text-slate-600">
                                  {row.address && (
                                    <span className="flex items-center gap-1.5 truncate" title={row.address}>
                                      <MapPin size={12} className="text-slate-400 shrink-0" />
                                      {row.address}
                                    </span>
                                  )}
                                  {row.email && (
                                    <span className="flex items-center gap-1.5 truncate" title={row.email}>
                                      <Mail size={12} className="text-slate-400 shrink-0" />
                                      {row.email}
                                    </span>
                                  )}
                                  {row.mobileNumber && (
                                    <span className="flex items-center gap-1.5">
                                      <Phone size={12} className="text-slate-400 shrink-0" />
                                      {row.mobileNumber}
                                    </span>
                                  )}
                                </div>
                              </td>

                              {/* TIN & Tax / Business Style */}
                              <td className="p-4 border-r border-slate-100 text-center text-xs">
                                <div className="font-mono text-slate-700">
                                  {row.tinNumber || "-"}
                                </div>
                                <div className="mt-1">
                                  {row.ewt ? (
                                    <span className="inline-flex items-center gap-0.5 px-2 py-0.5 text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 rounded">
                                      <ShieldCheck size={10} /> EWT
                                    </span>
                                  ) : (
                                    <span className="text-[10px] text-slate-400">No EWT</span>
                                  )}
                                </div>
                              </td>

                              {/* Price Type & Terms */}
                              <td className="p-4 border-r border-slate-100 text-center text-xs">
                                <div className="font-semibold text-slate-700">
                                  {row.priceTypeDisplay || row.priceType}
                                </div>
                                <div className="text-[11px] text-slate-500 mt-0.5">
                                  {row.termsDescription || "No Terms"}
                                </div>
                              </td>

                              {/* Salesmen tags + Change Indicator */}
                              <td className="p-4 border-r border-slate-100 text-xs max-w-[200px]">
                                <div className="flex flex-col gap-1">
                                  {row.salesmans.length > 0 ? (
                                    <div className="flex flex-wrap gap-1">
                                      {row.salesmans.map((s, idx) => (
                                        <span
                                          key={idx}
                                          className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] bg-indigo-50 text-indigo-700 border border-indigo-100 rounded"
                                        >
                                          <Tag size={9} /> {s.name}
                                        </span>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="text-slate-400 italic text-[11px]">
                                      None assigned
                                    </span>
                                  )}

                                  {row.isSalesmanChanged && (
                                    <span className="text-[10px] text-amber-700 font-medium inline-flex items-center gap-1">
                                      <RefreshCcw size={10} /> Salesmen modified
                                    </span>
                                  )}
                                </div>
                              </td>

                              {/* Status: NEW, UPDATED, UNCHANGED */}
                              <td className="p-4 text-center">
                                {row.status === "NEW" && (
                                  <span className="inline-block px-2.5 py-1 text-[10px] font-bold bg-green-100 text-green-800 rounded-full border border-green-200 shadow-sm">
                                    + NEW
                                  </span>
                                )}
                                {row.status === "UPDATED" && (
                                  <span className="inline-block px-2.5 py-1 text-[10px] font-bold bg-amber-100 text-amber-800 rounded-full border border-amber-200 shadow-sm">
                                    UPDATED
                                  </span>
                                )}
                                {row.status === "UNCHANGED" && (
                                  <span className="inline-block px-2.5 py-1 text-[10px] font-semibold bg-slate-100 text-slate-600 rounded-full border border-slate-200">
                                    UNCHANGED
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={6} className="p-12 text-center text-slate-400">
                              <p className="text-sm font-semibold">
                                No records found matching "{statusFilter}".
                              </p>
                              <button
                                onClick={() => setStatusFilter("ALL")}
                                className="mt-2 text-xs font-semibold text-indigo-600 hover:underline"
                              >
                                Reset filter to All
                              </button>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Summary Bar */}
                  <div className="bg-slate-50 p-3 border-t border-slate-200 flex justify-between items-center text-xs text-slate-500 px-6">
                    <span>
                      Total Customers in File: <strong>{fileData.length}</strong>
                    </span>
                    <div className="flex gap-4">
                      <span className="flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full bg-green-500" /> New:{" "}
                        <strong className="text-slate-700">{countNew}</strong>
                      </span>
                      <span className="flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full bg-amber-500" /> Updated:{" "}
                        <strong className="text-slate-700">{countUpdated}</strong>
                      </span>
                      <span className="flex items-center gap-1.5">
                        <span className="w-2.5 h-2.5 rounded-full bg-slate-400" /> Unchanged:{" "}
                        <strong className="text-slate-700">{countUnchanged}</strong>
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom Action Bar */}
              <div className="p-4 bg-white border-t border-slate-200 flex justify-between items-center z-20">
                <button
                  onClick={() => setStep(1)}
                  className="px-5 py-2.5 text-sm font-semibold text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors flex items-center gap-2"
                >
                  <ArrowLeft size={16} /> Re-upload
                </button>
                <button
                  onClick={() => validationErrors.length === 0 && setStep(3)}
                  disabled={validationErrors.length > 0}
                  className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-200 disabled:text-slate-400 disabled:shadow-none text-white px-8 py-2.5 rounded-xl font-bold text-sm shadow-lg shadow-indigo-200 transition-all transform active:scale-95"
                >
                  Next: Finalize <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}

          {/* STEP 3: CONFIRM & SAVE */}
          {step === 3 && (
            <div className="absolute inset-0 bg-slate-50 flex flex-col items-center justify-center p-6 transition-transform duration-500 ease-in-out z-30">
              <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-gradient-to-br from-indigo-100 to-white text-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm border border-indigo-50">
                    <Save size={32} />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-800">
                    Confirm Customer Import
                  </h2>
                  <p className="text-slate-500 mt-2 text-sm">
                    You are reviewing <strong>{fileData.length} customer records</strong>.
                  </p>
                </div>

                <div className="space-y-4">
                  {/* Status Breakdown Card */}
                  <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 grid grid-cols-3 divide-x divide-slate-200 text-center">
                    <div>
                      <div className="text-xs text-slate-400 font-semibold uppercase">
                        New
                      </div>
                      <div className="text-xl font-bold text-green-600 mt-0.5">
                        {countNew}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-400 font-semibold uppercase">
                        Updated
                      </div>
                      <div className="text-xl font-bold text-amber-600 mt-0.5">
                        {countUpdated}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-400 font-semibold uppercase">
                        Unchanged
                      </div>
                      <div className="text-xl font-bold text-slate-500 mt-0.5">
                        {countUnchanged}
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between items-end mb-1 ml-1">
                      <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider">
                        Upload Note / Remarks (Optional)
                      </label>
                      <span className="text-[10px] font-bold text-slate-400">
                        {note.length}/50
                      </span>
                    </div>
                    <input
                      type="text"
                      value={note}
                      maxLength={50}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="e.g. Q4 Batch Customer Sync"
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all text-slate-700"
                    />
                  </div>
                </div>

                <div className="mt-8 flex flex-col gap-3">
                  <button
                    onClick={handleFinalSave}
                    disabled={isProcessing}
                    className="w-full flex justify-center items-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300 text-white py-3.5 rounded-xl font-bold text-sm shadow-lg shadow-indigo-200 hover:shadow-xl transition-all active:scale-[0.98]"
                  >
                    {isProcessing ? (
                      <RefreshCw className="animate-spin" size={20} />
                    ) : (
                      <Check size={20} />
                    )}
                    {isProcessing ? "Saving Customers..." : "Confirm & Import Customers"}
                  </button>
                  <button
                    onClick={() => setStep(2)}
                    disabled={isProcessing}
                    className="w-full py-2.5 text-slate-500 hover:text-slate-800 font-semibold text-sm transition-colors"
                  >
                    Back to Review
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AllCustomers;
